<template>
  <div
    ref="containerRef"
    class="main-menu-root"
    @mouseenter="cancelLeaveTimer"
    @mouseleave="startLeaveTimer"
  >
    <a
      ref="triggerRef"
      class="main-menu-root__trigger"
      :class="{ 'main-menu-root__trigger--open': isOpen }"
      @mouseenter="toggle"
    >
      <span class="main-menu-root__trigger-text">{{ $tc('Menu', 2) }}</span>
    </a>
    <main-menu-panel
      v-if="isOpen"
      :items="menuChildren"
      :anchor-el="$refs.triggerRef"
      :level="0"
      @close-all="close"
      @navigate="close"
      @mouse-enter-panel="cancelLeaveTimer"
      @mouse-leave-panel="startLeaveTimer"
      class="ml-1"
    />
  </div>
</template>

<script>
import {
  ref,
  computed,
  provide,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api'
import MainMenuPanel from './MainMenuPanel.vue'

export default {
  name: 'MainMenu',
  components: {
    MainMenuPanel,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const isOpen = ref(false)
    const containerRef = ref(null)
    const triggerRef = ref(null)
    const closeAllTick = ref(0)
    let leaveTimer = null

    provide('mainMenuControl', { closeAllTick })

    const menuChildren = computed(() => {
      // items is the full navMenuItems array; we need the first element's children
      if (
        props.items &&
        props.items.length > 0 &&
        props.items[0].children
      ) {
        return props.items[0].children
      }
      return props.items || []
    })

    const toggle = () => {
      if (isOpen.value) {
        close()
      } else {
        open()
      }
    }

    const open = () => {
      isOpen.value = true
    }

    const close = () => {
      isOpen.value = false
      closeAllTick.value += 1
      cancelLeaveTimer()
    }

    // 500ms leave timer
    const startLeaveTimer = () => {
      cancelLeaveTimer()
      leaveTimer = setTimeout(() => {
        close()
        leaveTimer = null
      }, 5000)
    }

    const cancelLeaveTimer = () => {
      if (leaveTimer) {
        clearTimeout(leaveTimer)
        leaveTimer = null
      }
    }

    // Click-outside handler
    const onDocumentClick = (e) => {
      if (!isOpen.value) return
      const container = containerRef.value
      if (container && !container.contains(e.target)) {
        // Check if clicked on a panel (which may be outside the container due to fixed positioning)
        const panels = document.querySelectorAll('.main-menu-panel')
        for (const panel of panels) {
          if (panel.contains(e.target)) return
        }
        close()
      }
    }

    onMounted(() => {
      document.addEventListener('click', onDocumentClick, true)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', onDocumentClick, true)
      cancelLeaveTimer()
    })

    return {
      isOpen,
      containerRef,
      triggerRef,
      menuChildren,
      toggle,
      close,
      startLeaveTimer,
      cancelLeaveTimer,
    }
  },
}
</script>

<style scoped>
.main-menu-root {
  display: inline-flex;
  align-items: center;
  position: relative;
}

.main-menu-root__trigger {
  display: flex;
  align-items: center;
  padding: 0.6rem 1rem 0.6rem 1rem;
  color: #fff;
  cursor: pointer;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  transition: opacity 0.15s ease;
  user-select: none;
}

.main-menu-root__trigger-text {
  white-space: nowrap;
  color: #fff;
}
</style>
